import { FC } from 'react';
import { VisibilityOffRounded } from '@mui/icons-material';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import TeamMemberAvatar from '../../TeamMemberAvatar';
import { formatTeamMemberOverflowList, sanitizeTeamMemberName } from '../../utils';
import './TeamTasksHiddenMembersFooter.scss';

interface HiddenTeamMember {
  id: string;
  name: string;
  initials: string;
  photoUrl: string;
}

interface TeamTasksHiddenMembersFooterProps {
  segment: 'team' | 'shared';
  hiddenMembers: HiddenTeamMember[];
  isKanbanInset?: boolean;
}

const TeamTasksHiddenMembersFooter: FC<TeamTasksHiddenMembersFooterProps> = ({
  segment,
  hiddenMembers,
  isKanbanInset = false,
}) => {
  const { t } = useTranslation();

  if (segment !== 'shared' || !hiddenMembers.length) {
    return null;
  }

  const hiddenCopy = formatTeamMemberOverflowList(
    hiddenMembers.map((member) => sanitizeTeamMemberName(member.name)),
    3,
    t
  );

  return (
    <div
      className={clsx(
        'team-tasks-view__hidden-members',
        isKanbanInset && 'team-tasks-view__hidden-members--kanban'
      )}
    >
      <span className="team-tasks-view__hidden-icon" aria-hidden="true">
        <VisibilityOffRounded />
      </span>
      <div className="team-tasks-view__hidden-copy">
        <Typography variant="body2" component="p" className="team-tasks-view__hidden-title">
          {t('team.tasks.hiddenMembersTitle', { count: hiddenMembers.length })}
        </Typography>
        <Typography variant="subtitle2" component="p" className="team-tasks-view__hidden-subtitle">
          {t('team.tasks.hiddenMembersDescription', {
            names: hiddenCopy,
            count: hiddenMembers.length,
          })}
        </Typography>
      </div>
      <div className="team-tasks-view__hidden-avatars" aria-hidden="true">
        {hiddenMembers.slice(0, 4).map((member) => (
          <TeamMemberAvatar
            key={member.id}
            name={member.name}
            initials={member.initials}
            photoUrl={member.photoUrl}
            size="medium"
          />
        ))}
      </div>
    </div>
  );
};

export default TeamTasksHiddenMembersFooter;
